import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin } from 'lucide-react';
import { companyInfo } from '../mock';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  // Updated service sequence
  const footerLinks = {
    services: [
      { label: 'Patient Care', href: '#services' },
      { label: 'Integrated Medicine', href: '#services' },
      { label: 'AI Powered Education & Research', href: '#services' },
      { label: 'AI Powered Preventive Care', href: '#services' },
      { label: 'Medicine & Herbal Supply Chain', href: '#services' },
      { label: 'RHID™ Software', href: '#services' },
      { label: 'Expert Network & Partnerships', href: '#services' },
      { label: 'Equipment Procurement', href: '#services' },
      { label: 'Hospital Interior Design & Vastu', href: '#services' },
    ],
    company: [
      { labelKey: 'footer.aboutUs', href: '#why-choose-us' },
      { labelKey: 'footer.ourProcess', href: '#process' },
      { labelKey: 'footer.ourFounder', path: '/dr-kumar-profile', type: 'route' },
      { labelKey: 'footer.careers', path: '/careers', type: 'route' },
    ],
    legal: [
      { labelKey: 'footer.privacyPolicy', path: '/privacy-policy', type: 'route' },
      { labelKey: 'footer.termsOfService', path: '/terms-of-service', type: 'route' },
      { labelKey: 'footer.cookiePolicy', path: '/cookie-policy', type: 'route' },
      { labelKey: 'footer.compliance', path: '/compliance', type: 'route' },
    ],
  };

  const scrollToSection = (sectionId) => {
    if (sectionId.startsWith('#')) {
      const element = document.getElementById(sectionId.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleNavigation = (item) => {
    if (item.type === 'route') {
      navigate(item.path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      scrollToSection(item.href);
    }
  };

  return (
    <footer
      style={{
        background: '#111827',
        color: 'white',
        padding: '4rem 1.2rem 2rem',
        position: 'relative',
      }}
    >
      <div className="container">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '3rem',
            marginBottom: '3rem',
          }}
        >
          <div>
            <img
              src="logo.png"
              alt="Ayurmed Global Logo"
              style={{
                height: '60px',
                width: 'auto',
                marginBottom: '1.5rem',
              }}
            />
            <p
              className="body-small"
              style={{
                color: 'rgba(255, 255, 255, 0.8)',
                marginBottom: '1.5rem',
                lineHeight: '1.6',
              }}
            >
              {t('footer.description')}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <MapPin size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                <span className="body-small" style={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: '1.5' }}>
                  {companyInfo.address}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Mail size={16} />
                <span className="body-small" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {companyInfo.email}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Phone size={16} />
                <span className="body-small" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {companyInfo.phone}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '1rem' }}>
              {t('footer.services')}
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {footerLinks.services.map((link) => (
                <li key={link.label} style={{ marginBottom: '0.5rem' }}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      padding: 0,
                      transition: 'color 0.2s ease',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => (e.target.style.color = 'white')}
                    onMouseLeave={(e) => (e.target.style.color = 'rgba(255, 255, 255, 0.7)')}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '1rem' }}>
              {t('footer.company')}
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {footerLinks.company.map((link) => (
                <li key={link.labelKey} style={{ marginBottom: '0.75rem' }}>
                  <button
                    onClick={() => handleNavigation(link)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      padding: 0,
                      transition: 'color 0.2s ease',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => (e.target.style.color = 'white')}
                    onMouseLeave={(e) => (e.target.style.color = 'rgba(255, 255, 255, 0.7)')}
                  >
                    {t(link.labelKey)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div style={{ height: '1px', background: 'rgba(255, 255, 255, 0.2)', marginBottom: '2rem' }} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1.5rem',
          }}
        >
          <div className="body-small" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            © {currentYear} {companyInfo.name}. {t('footer.rights')}
          </div>

          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {footerLinks.legal.map((link) => (
              <button
                key={link.labelKey}
                onClick={() => handleNavigation(link)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => (e.target.style.color = 'white')}
                onMouseLeave={(e) => (e.target.style.color = 'rgba(255, 255, 255, 0.6)')}
              >
                {t(link.labelKey)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
